// ============================================================================
// TASKAPPCONTAINERS.JS - COMPONENTES DE RUTA DE TASKAPP A NIVEL DE MÓDULO
// ============================================================================
// Antes vivían DENTRO de TaskAppInner: cada re-render creaba un componente
// nuevo y React desmontaba/remontaba toda la pantalla (pérdida de foco en
// /task/create, refetch de épicas en bucle con tenants sin épicas...).
// A nivel de módulo la identidad es estable y solo se re-renderiza.

import React, { useState, useEffect, useMemo, useCallback, useRef, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { taskMatchesParam } from '../utils/taskRef';
import PinnedDocs from './PinnedDocs';
import ActiveSprintPanel from './ActiveSprintPanel';
import TaskDetail from './TaskDetail';
// task-form.css se queda en el bundle inicial aunque el formulario vaya lazy:
// tiene reglas globales (.btn-primary, .section-title, .tag, .field-error...)
// que hoy pintan también pantallas del main. Importado aquí conserva su
// posición original en la cascada de main.css (revisión 2026-10, MEDIA-3).
import '../styles/task-form.css';
import lazyRetry from '../utils/lazyRetry';

// Formulario de tarea bajo demanda (revisión 2026-10, informe 05, MEDIA-3). Las
// rutas create y edit lo envuelven en ModuleShell (TaskApp.js), que pone el
// Suspense dentro del hijo de presencia de AnimatePresence.
const TaskFormPage = lazy(() => lazyRetry(() => import('./TaskFormPage'), 'TaskFormPage'));

// Componente de carga
export const LoadingOverlay = () => (
  <div className="loading-overlay">
    <div className="loading-content">
      <Loader2 size={32} className="loading-spinner" />
      <p className="loading-text">Cargando tareas...</p>
    </div>
  </div>
);

// Componente de error
export const ErrorDisplay = ({ error, onRetry }) => (
  <div className="error-display">
    <div className="error-content">
      <h3>⚠️ Error en la aplicación</h3>
      <p>{error}</p>
      <button
        className="btn-retry"
        onClick={() => {
          onRetry();
          window.location.reload();
        }}
      >
        Reintentar
      </button>
    </div>
  </div>
);

// Dashboard principal
export const TasksDashboard = ({ onTaskSelect, onOpenDocumentation }) => (
  <div className="tasks-home">
    <PinnedDocs onOpenDocumentation={onOpenDocumentation} />
    <ActiveSprintPanel
      onTaskSelect={onTaskSelect}
      onOpenDocumentation={onOpenDocumentation}
    />
  </div>
);

// Crear nueva tarea
export const TaskCreateContainer = () => <TaskFormPage />;

// Editar tarea existente
export const TaskEditContainer = () => <TaskFormPage />;

function TaskDetailContainerInner({ taskId, isInitialized, navigate, currentTask, tasks, setCurrentTask, fetchTask, isTransitioning, handleBackToTable }) {
  const [isLoadingTask, setIsLoadingTask] = useState(!isInitialized);
  const [taskError, setTaskError] = useState(null);
  
  // REF para prevenir actualizaciones durante operaciones internas
  const isUpdatingInternally = useRef(false);
  
  // 🔧 MEMO para estabilizar la tarea que se pasa al componente
  // Esto previene re-renders innecesarios del TaskDetail
  const stableTask = useMemo(() => {
    if (!taskId) return null;

    // El parámetro de URL puede ser el id legacy o la referencia (CERTI-106)
    if (currentTask && taskMatchesParam(currentTask, taskId)) {
      console.log('TaskApp: Using currentTask from store:', currentTask.id);
      return currentTask;
    }

    // Buscar en la lista de tareas
    const foundTask = tasks.find(t => taskMatchesParam(t, taskId));
    if (foundTask) {
      console.log('TaskApp: Found task in tasks array:', foundTask.id);
      return foundTask;
    }

    console.log('TaskApp: No task found for ID:', taskId);
    return null;
  }, [taskId, currentTask, tasks]);

  // 🔧 CALLBACK estable para manejar actualizaciones de tareas
  const handleTaskUpdate = useCallback((event) => {
    // El evento siempre lleva el id real; la URL puede llevar la referencia
    // (CERTI-106), así que comparamos contra el id de la tarea ya resuelta
    const realTaskId = stableTask?.id;
    if (event.detail?.taskId && event.detail.taskId === realTaskId && !isUpdatingInternally.current) {
      console.log('TaskApp: Received external task update for:', realTaskId);

      // Marcar que estamos procesando una actualización
      isUpdatingInternally.current = true;

      setTimeout(() => {
        const updatedTask = tasks.find(t => t.id === realTaskId);
        if (updatedTask) {
          console.log('TaskApp: Syncing updated task:', updatedTask.id);
          setCurrentTask(updatedTask);
        }

        // Permitir actualizaciones nuevamente después del procesamiento
        isUpdatingInternally.current = false;
      }, 100);
    }
  }, [stableTask, tasks, setCurrentTask]);

  // Listener para eventos de actualización de tareas - ESTABLE
  useEffect(() => {
    window.addEventListener('taskUpdated', handleTaskUpdate);
    return () => window.removeEventListener('taskUpdated', handleTaskUpdate);
  }, [handleTaskUpdate]);

  // 🔧 EFFECT para carga inicial - MEJORADO sin bucles
  useEffect(() => {
    let mounted = true;
    
    const loadTask = async () => {
      // Si la app no está inicializada, esperar
      if (!isInitialized) {
        return;
      }
      
      // Si ya tenemos la tarea estable, mostrarla YA y traer en segundo plano
      // la versión completa: el listado viaja "ligero" (sin afectacion/solucion/
      // historial_estados) y el detalle necesita esos campos.
      if (stableTask || !taskId) {
        setIsLoadingTask(false);
        if (stableTask && taskId && fetchTask && stableTask.afectacion === undefined) {
          fetchTask(stableTask.id).then((result) => {
            if (mounted && result?.success) setCurrentTask(result.data);
          }).catch(() => {});
        }
        return;
      }
      
      setIsLoadingTask(true);
      setTaskError(null);
      
      console.log('TaskApp: Loading task from API:', taskId);
      
      try {
        if (fetchTask) {
          const result = await fetchTask(taskId);
          
          if (!mounted) return; // Componente desmontado durante carga
          
          if (result.success) {
            console.log('TaskApp: Task loaded successfully:', result.data.id);
            setCurrentTask(result.data);
          } else {
            console.error('TaskApp: Task load failed:', result.error);
            setTaskError(result.error);
            // Redirigir al dashboard después de un momento
            setTimeout(() => navigate('/task'), 2000);
          }
        } else {
          console.log('TaskApp: No fetchTask method available');
          setTaskError('Función de carga no disponible');
          setTimeout(() => navigate('/task'), 2000);
        }
      } catch (error) {
        if (!mounted) return;
        
        console.error('TaskApp: Error loading task:', error);
        setTaskError(error.message);
        setTimeout(() => navigate('/task'), 2000);
      } finally {
        if (mounted) {
          setIsLoadingTask(false);
        }
      }
    };
    
    loadTask();
    
    return () => {
      mounted = false;
    };
  }, [taskId, fetchTask, setCurrentTask, navigate, isInitialized, stableTask]);

  // Si la app no está inicializada, mostrar loading
  if (!isInitialized) {
    return (
      <div className="task-detail-loading">
        <Loader2 size={32} className="loading-spinner" />
        <p>Inicializando aplicación...</p>
      </div>
    );
  }

  // Estados de carga y error
  if (isLoadingTask) {
    return (
      <div className="task-detail-loading">
        <Loader2 size={32} className="loading-spinner" />
        <p>Cargando detalles de la tarea...</p>
      </div>
    );
  }

  if (taskError) {
    return (
      <div className="task-detail-error">
        <h3>❌ Error al cargar tarea</h3>
        <p>{taskError}</p>
        <button 
          className="btn-primary"
          onClick={() => navigate('/task')}
        >
          Volver al dashboard
        </button>
      </div>
    );
  }

  if (!stableTask) {
    return (
      <div className="task-detail-error">
        <h3>❌ Tarea no encontrada</h3>
        <p>La tarea solicitada no existe o no se pudo cargar.</p>
        <button 
          className="btn-primary"
          onClick={() => navigate('/task')}
        >
          Volver al dashboard
        </button>
      </div>
    );
  }

  console.log('TaskApp: Rendering TaskDetail with stable task:', stableTask.id);

  return (
    <TaskDetail 
      task={stableTask}
      onBack={handleBackToTable}
      isTransitioning={isTransitioning}
    />
  );
}

// Wrapper de ruta: extrae el taskId de la URL y delega en el contenedor
export const TaskDetailContainer = (props) => {
  const { taskId } = useParams();
  return <TaskDetailContainerInner taskId={taskId} {...props} />;
};
